import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Header, Footer } from "../components/Layout";
import { VEHICLE_LIST } from "../data/vehicles";
import { fadeInUp, stagger, HERO_TITLE_EARLY, HERO_SUB } from "../lib/animations";

const VEHICLES_HERO_EYEBROW = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: 0.2 }
};

const HeroSection = () => (
  <section className="pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-8" data-testid="vehicles-hero">
    <div className="max-w-[1400px] mx-auto">
      <motion.p 
        className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-[#FF2A00] mb-4"
        {...VEHICLES_HERO_EYEBROW}
      >
        The Lineup
      </motion.p>
      <motion.h1 
        className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight mb-6"
        {...HERO_TITLE_EARLY}
      >
        THE MACHINES
      </motion.h1>
      <motion.p 
        className="text-lg md:text-xl text-[#A3A3A3] max-w-2xl"
        {...HERO_SUB}
      >
        Eight machines. One philosophy. From hypercar to work truck, 
        every Keller Motors machine shares the same commitment to mechanical purity.
      </motion.p>
    </div>
  </section>
);

const VehicleGrid = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section ref={ref} className="pb-24 md:pb-40 px-6 md:px-8" data-testid="vehicle-grid">
      <div className="max-w-[1400px] mx-auto">
        <motion.div 
          className="grid md:grid-cols-2 gap-6"
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          {VEHICLE_LIST.map((vehicle, index) => (
            <motion.div
              key={vehicle.id}
              variants={fadeInUp}
              className={`group ${index === 0 ? 'md:col-span-2' : ''}`}
            >
              <Link to={`/vehicles/${vehicle.id}`}>
                <div className="border border-[#262626] hover:border-[#FF2A00] transition-colors duration-300 overflow-hidden">
                  <div className={`relative overflow-hidden bg-[#0F0F0F] ${index === 0 ? 'aspect-[21/9]' : 'aspect-[16/9]'}`}>
                    {vehicle.images[0] ? (
                      <img 
                        src={vehicle.images[0]}
                        alt={vehicle.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center">
                          <p className="font-mono text-xs uppercase tracking-wider text-[#A3A3A3] mb-2">Design in Progress</p>
                          <p className="font-display text-2xl text-[#262626]">{vehicle.name}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Overlay info */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div>
                          <p className="font-mono text-xs uppercase tracking-wider text-[#FF2A00] mb-2">{vehicle.edition}</p>
                          <h3 className={`font-display tracking-tight ${index === 0 ? 'text-4xl md:text-5xl' : 'text-3xl'}`}>
                            {vehicle.name}
                          </h3>
                          <p className="text-sm text-[#A3A3A3] mt-2">{vehicle.tagline}</p>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="font-display text-2xl">{vehicle.price}</p>
                            <p className="font-mono text-xs text-[#A3A3A3]">{vehicle.specs.power}</p>
                          </div>
                          <div className="w-12 h-12 border border-white/30 flex items-center justify-center group-hover:bg-[#FF2A00] group-hover:border-[#FF2A00] transition-colors duration-300">
                            <ArrowRight className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const ComparisonNote = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 px-6 md:px-8 bg-[#0F0F0F]" data-testid="comparison-note">
      <div className="max-w-[1400px] mx-auto text-center">
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={stagger}
        >
          <motion.p variants={fadeInUp} className="font-mono text-xs tracking-[0.2em] uppercase text-[#FF2A00] mb-6">
            The Math
          </motion.p>
          <motion.h2 variants={fadeInUp} className="font-display text-4xl md:text-6xl tracking-tight mb-8">
            800 BHP FOR $30,000
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-lg text-[#A3A3A3] max-w-3xl mx-auto">
            While others charge $300,000+ for similar performance wrapped in complexity, 
            we deliver raw, honest power at a fraction of the cost. No gimmicks. No planned obsolescence. 
            Just adrenaline.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default function VehiclesPage() {
  return (
    <main className="min-h-screen bg-[#050505]">
      <Header />
      <HeroSection />
      <VehicleGrid />
      <ComparisonNote />
      <Footer />
    </main>
  );
}
